/**
 * Client-side calendar reminder engine: a small loop that polls the calendar service once a
 * minute and fires a browser notification for every event starting within the user's lead
 * window (5/10/15 minutes). Purely client-side - the control service learns nothing; media
 * and scheduling stay untouched.
 *
 * The engine is dependency-injected end to end (clock, fetch, storage, notification sink,
 * permission source, timers), so the whole behavior is testable without a browser and without
 * real time. No module-level singletons: the app shell owns one instance per mount and stops
 * it on unmount.
 *
 * Each tick: if reminders are disabled, permission is not granted or there is no SSO token,
 * the tick is inert (no request). Otherwise events are listed for [now, now + lead + 1 min]
 * (the extra minute keeps an event that walks into the window during the polling gap
 * reachable) and every event whose start falls inside (now, now + lead] is announced exactly
 * once. Exactly-once is a localStorage set of `${event.id}:${occurrenceStartsAt ?? startsAt}`
 * keys with a 24 h time-to-live, so the set cannot grow unbounded. Any failure of a tick
 * (network, 401 after the client's own refresh-retry, 503 calendar_disabled, a malformed
 * response) is swallowed: the loop stays alive for the next tick.
 */

import { listEvents } from './api';
import type { ViewPrefStorage } from './viewPref';

/** localStorage key of the fired-reminder dedup set: `{ [key]: recordedAtEpochMs }`. */
export const REMINDER_SEEN_STORAGE_KEY = 'vc.reminderSeen';

/** Poll cadence; the first tick fires immediately on start. */
export const REMINDER_TICK_MS = 60_000;

/** Dedup keys older than this are pruned on the next write. */
const SEEN_TTL_MS = 24 * 60 * 60 * 1000;

/** Extra fetch window beyond the lead, in ms - see the module comment. */
const FETCH_EXTRA_MS = 60_000;

/** The subset of the DOM NotificationPermission the engine reasons about (kept structural
 * so the engine itself has no DOM dependency). */
export type NotificationPermissionState = 'granted' | 'denied' | 'default';

/** The reminder-relevant slice of the notification preferences. */
export interface ReminderPrefs {
  readonly enabled: boolean;
  readonly leadMinutes: number;
}

/** Structural timer seams; the DOM timers satisfy both. */
export type ReminderSetInterval = (handler: () => void, timeoutMs: number) => number;
export type ReminderClearInterval = (handle: number) => void;

/** Everything the loop needs from the outside; all injectable, none captured globally. */
export interface ReminderDeps {
  /** Current notification preferences (re-read every tick, so a settings change applies live). */
  readonly prefs: () => ReminderPrefs;
  /** The calendar bearer token, or null when nobody is signed in. */
  readonly token: () => string | null;
  readonly fetchImpl: typeof fetch;
  readonly now: () => Date;
  /** The notification sink: title and a preformatted body (the localized start time). */
  readonly notify: (title: string, body: string) => void;
  readonly permission: () => NotificationPermissionState;
  /** localStorage-backed store for the dedup set. */
  readonly storage: ViewPrefStorage;
  readonly setIntervalImpl?: ReminderSetInterval;
  readonly clearIntervalImpl?: ReminderClearInterval;
}

/** Formats a start time as a 24-hour "HH:MM" in the local time zone. */
export function formatTimeHHMM(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Reads and prunes the dedup set; anything malformed degrades to an empty set. */
function loadSeen(storage: ViewPrefStorage, nowMs: number): Record<string, number> {
  try {
    const raw = storage.getItem(REMINDER_SEEN_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return {};
    }
    const seen: Record<string, number> = {};
    const oldestAllowed = nowMs - SEEN_TTL_MS;
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'number' && Number.isFinite(value) && value > oldestAllowed) {
        seen[key] = value;
      }
    }
    return seen;
  } catch {
    return {};
  }
}

function saveSeen(storage: ViewPrefStorage, seen: Record<string, number>): void {
  try {
    storage.setItem(REMINDER_SEEN_STORAGE_KEY, JSON.stringify(seen));
  } catch {
    // dedup state is best effort: a failed write can at worst re-notify once
  }
}

/** One event's effective start: the occurrence window for expansions, the base start otherwise. */
function eventStartMs(event: { readonly startsAt: string; readonly occurrenceStartsAt: string | null }): number {
  return Date.parse(event.occurrenceStartsAt ?? event.startsAt);
}

interface TickState {
  stopped: boolean;
}

/** One poll: gated by prefs/permission/token, then one list call and the notify pass. */
async function runTick(deps: ReminderDeps, state: TickState): Promise<void> {
  const prefs = deps.prefs();
  if (!prefs.enabled || deps.permission() !== 'granted') {
    return;
  }
  const bearer = deps.token();
  if (bearer === null) {
    return;
  }
  const at = deps.now();
  const leadMs = Math.max(0, prefs.leadMinutes) * 60_000;
  const windowEnd = at.getTime() + leadMs + FETCH_EXTRA_MS;
  const events = await listEvents(
    bearer,
    { from: at.toISOString(), to: new Date(windowEnd).toISOString() },
    deps.fetchImpl,
  );
  if (state.stopped) {
    return;
  }
  const seen = loadSeen(deps.storage, at.getTime());
  let dirty = false;
  for (const event of events) {
    const startMs = eventStartMs(event);
    if (Number.isNaN(startMs)) {
      continue;
    }
    // strictly after now (a start this minute is gone) and within the lead window
    if (startMs <= at.getTime() || startMs > at.getTime() + leadMs) {
      continue;
    }
    const key = `${event.id}:${event.occurrenceStartsAt ?? event.startsAt}`;
    if (seen[key] !== undefined) {
      continue;
    }
    seen[key] = at.getTime();
    dirty = true;
    deps.notify(event.title, formatTimeHHMM(new Date(startMs)));
  }
  if (dirty) {
    saveSeen(deps.storage, seen);
  }
}

/**
 * Starts the loop: one immediate tick, then one per {@link REMINDER_TICK_MS}. Returns the
 * stop function - it cancels the interval and marks the loop stopped so an in-flight tick
 * still under way when stop() ran stays silent.
 */
export function startReminderLoop(deps: ReminderDeps): () => void {
  const setIntervalImpl: ReminderSetInterval =
    deps.setIntervalImpl ?? ((handler, timeoutMs) => window.setInterval(handler, timeoutMs));
  const clearIntervalImpl: ReminderClearInterval =
    deps.clearIntervalImpl ?? ((handle) => window.clearInterval(handle));
  const state: TickState = { stopped: false };
  const guardedTick = (): void => {
    void runTick(deps, state).catch(() => {
      // network / 401 / 503 calendar_disabled / malformed response - skip the tick silently
    });
  };
  guardedTick();
  const handle = setIntervalImpl(guardedTick, REMINDER_TICK_MS);
  return () => {
    state.stopped = true;
    clearIntervalImpl(handle);
  };
}
